import { useEffect, useRef, useState } from 'react';
import type { IReportInfo } from '@/shared/model';
import { useReportStore } from '@/entities/report';
import type { HrChartHandle } from './hrChart';

/**
 * Backs the artifact areas tab, supplying the report identifiers and ECG data its HR chart needs. Reads the report info and ECG data map from the store, counts idle seconds on a one-second interval that any mouse move resets, rolls the counter over at five minutes, and exposes a ref-based arrow handler for the chart.
 *
 * @returns An object carrying the report identifiers, the ECG data map, the chart ref and the arrow handler.
 */
const useArtifactAreas = () => {
  const { profileId, studyId, startReport, stopReport, timezoneOffset } = useReportStore(
    (s) => s.reportInfo,
  ) as IReportInfo;
  const ecgDataMap = useReportStore((s) => s.ecgDataMap);

  const [timeTracking, setTimeTracking] = useState(0);

  const hrChartRef = useRef<HrChartHandle>(null);
  const timeTrackingInterval = useRef<ReturnType<typeof setInterval> | null>(null);

  const TIME_TRACKING_DURATION = 5 * 60;

  /**
   * Forwards an arrow-button press to the HR chart under the artifact areas tab. Calls the chart handle's own arrow handler, and does nothing while the ref is unattached.
   *
   * @param isNext - true to step forward, false to step back.
   * @returns Nothing.
   */
  const handleClickArrowButton = (isNext = false) => {
    if (hrChartRef.current) {
      hrChartRef.current.handleClickArrowButton(isNext);
    }
  };

  /**
   * Resets the idle timer whenever the pointer moves over the artifact areas tab. Sets the tracked second count back to zero.
   *
   * @returns Nothing.
   */
  const handleClearTimeTracking = () => {
    setTimeTracking(0);
  };

  useEffect(() => {
    window.addEventListener('mousemove', handleClearTimeTracking);
    return () => {
      window.removeEventListener('mousemove', handleClearTimeTracking);
    };
  }, []);

  useEffect(() => {
    if (timeTracking === TIME_TRACKING_DURATION) {
      setTimeTracking(0);

      // TODO: handle send data layer
    } else {
      timeTrackingInterval.current = setInterval(() => {
        setTimeTracking((prevState) => prevState + 1);
      }, 1000);
    }
    return () => {
      clearInterval(timeTrackingInterval.current);
      timeTrackingInterval.current = null;
    };
  }, [timeTracking]);

  return {
    profileId,
    studyId,
    startReport,
    stopReport,
    timezoneOffset,
    ecgDataMap,
    hrChartRef,
    handleClickArrowButton,
  };
};

export default useArtifactAreas;
